import * as React from 'react'

import { Table, Text, useToggle } from '@factorialco/deprecated-design-system'
import moment from 'moment'

import { observer } from 'mobx-react'

import hasFeature from 'modules/core/lib/hasFeature'
import i18n from 'modules/core/lib/i18n'
import { getFullDisplayName } from 'modules/core/lib/userDisplayName'
import users from 'modules/core/store/collections/users'
import type User from 'modules/core/store/models/User'

import EmployeeCell from 'modules/audit_log/pages/settings/AuditLogPage/_Table/_EmployeeCell'
import type { Audit } from 'modules/audit_log/pages/settings/AuditLogPage/_Table/constants'
import employees from 'modules/employees/store/collections/employees'

import EventData from './eventData'
import logo from './factorial-logo.svg'

interface Props {
  event: Audit
  totalSpan: number
}

const EventRow = ({ event, totalSpan }: Props) => {
  const [open, toggle] = useToggle(false)
  const { date, access_id, data, event_name } = event

  const getUser = (id: number) => {
    return users.find({
      id,
    })
  }

  const getEmployee = (id: number) => {
    return employees.find({
      id,
    })
  }

  const getTarget = () => {
    const userId = data.snapshot?.access_id

    if (userId) {
      return getUser(userId)
    }

    const employeeId = data.snapshot?.employee_id

    if (employeeId) {
      const employee = getEmployee(employeeId)
      if (employee) {
        const accessId = employee.attributes.access_id
        const user = users.get(accessId)
        if (!user) {
          throw Error(`Error getting users with id = ${accessId}`)
        }
        return user
      }
    }
  }

  const author = getUser(access_id)
  const target = getTarget()
  const fullDate = new Date(date)
  const dateFormatted = moment(fullDate).format('YYYY-MM-DD HH:mm').toString()

  const getName = (user: User | undefined) => {
    return user
      ? getFullDisplayName({
          firstName: user.attributes.first_name,
          lastName: user.attributes.last_name,
        })
      : i18n.t('settings.audit_log.log_detail.factorial_system')
  }

  const getAvatar = (user: User | undefined) => {
    return user ? user.attributes.avatar : logo
  }

  const becomeId = event.become_id
  const becomeUser = becomeId ? getUser(becomeId) : undefined
  const becomeEnabled = hasFeature('become_enabled')

  const content = () => {
    return (
      <>
        <Table.Cell colSpan={5}>
          <EmployeeCell url={getAvatar(author) ?? ''} name={getName(author)} />
        </Table.Cell>
        <Table.Cell colSpan={5}>
          <Text.Normal weight="medium">{event_name}</Text.Normal>
        </Table.Cell>
        {becomeEnabled && (
          <Table.Cell colSpan={5}>
            {event.become_id ? (
              <EmployeeCell url={getAvatar(becomeUser) ?? ''} name={getName(becomeUser)} />
            ) : (
              <Text.Normal weight="medium">---</Text.Normal>
            )}
          </Table.Cell>
        )}
        <Table.Cell colSpan={5}>
          {target ? (
            <EmployeeCell url={getAvatar(target) ?? ''} name={getName(target)} />
          ) : (
            <Text.Normal weight="medium">---</Text.Normal>
          )}
        </Table.Cell>
        <Table.Cell colSpan={5}>
          <Text.Normal weight="medium">{dateFormatted}</Text.Normal>
        </Table.Cell>
      </>
    )
  }

  return (
    <Table.ExpandableRow
      content={content}
      toggle={toggle(!open)}
      opened={open}
      iconColSpan={2}
      icon="chevron"
      iconPosition="right"
    >
      <Table.Row>
        <Table.Cell colSpan={totalSpan} isExplotable>
          <EventData data={data} />
        </Table.Cell>
      </Table.Row>
    </Table.ExpandableRow>
  )
}

export default observer(EventRow)
